import { useAuth } from "@/src/contexts/auth.context";
import { BlocksApiNews } from "@/src/models/news.model";
import { useGlobalNewsQuery } from "@/src/services/api/news.api";
import {
  useAllNews,
  useDeleteNews,
  useDraftNews,
  useNewsCategoriesQuery,
  useNewsReadState,
  useNewsSaveState,
  usePinnedNews,
  useTogglePinNews,
} from "@/src/services/api/news/news.hooks";
import { useToggleSaved } from "@/src/services/profile/profile.hooks";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import { Platform, useWindowDimensions } from "react-native";
import { formatUserName } from "@/src/utils/string.utils";

export enum NewsTab {
  LATEST_NEWS = "LABEL_LATEST_NEWS",
  ALL_NEWS = "LABEL_ALL_NEWS",
  SAVED_NEWS = "LABEL_SAVED_NEWS",
  DRAFT_NEWS = "LABEL_DRAFT_NEWS",
  PINNED_NEWS = "LABEL_PINNED_NEWS",
}

export const isWeb = Platform.OS === "web";

// Read/unread filter scoped to the Pinned News tab. Default is "unread" so a
// pinned item drops out of view on its own once opened, per the HR ask —
// "All" is the filter-back-in escape hatch, not the default.
export type PinnedReadFilter = "unread" | "all";

const LATEST_NEWS_COUNT = 6;

export const resolveAuthorName = (newsItem: BlocksApiNews): string => {
  if (!newsItem.authorSnapshot) return newsItem.CreatedBy;
  const fullName = formatUserName(
    newsItem.authorSnapshot.firstName,
    newsItem.authorSnapshot.lastName,
  );
  return fullName || newsItem.CreatedBy;
};

export const formatArticleDate = (dateStr: string): string =>
  dateStr
    ? new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "";


type LatestNewsProps = Readonly<{
  groupId?: string
}>;

export function useLatestNews({ groupId }: LatestNewsProps = {}) {
  const { user, isAdmin } = useAuth();
  const userId = user?.itemId ?? "";

  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState<NewsTab>(NewsTab.LATEST_NEWS);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [deleteTarget, setDeleteTarget] = useState<BlocksApiNews | null>(null);
  const [localDeletedIds, setLocalDeletedIds] = useState<string[]>([]);
  const [localUnsavedIds, setLocalUnsavedIds] = useState<string[]>([]);
  const [pinnedReadFilter, setPinnedReadFilter] =
    useState<PinnedReadFilter>("unread");

  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const {
    data: globalNewsData,
    isLoading: isLoadingGlobalNews,
    isError: isGlobalNewsError,
  } = useGlobalNewsQuery();

  const allTabs: NewsTab[] = isMobile
    ? [NewsTab.ALL_NEWS, NewsTab.SAVED_NEWS]
    : [NewsTab.LATEST_NEWS, NewsTab.ALL_NEWS, NewsTab.SAVED_NEWS];

  const { data: pinnedNews = [], isLoading: isLoadingPinnedNews } =
    usePinnedNews(groupId);

  // Pinned sits permanently alongside the other admin tabs. It used to appear
  // only while something was pinned (CR-B1-02), which made the tab row change
  // shape under the user; an admin looking for the pinned list could not find
  // it precisely when it was empty. With nothing pinned the tab now renders the
  // screen's standard "No News" empty state, so it is never a dead end.
  const tabs: NewsTab[] = isAdmin
    ? isMobile
      ? [
          NewsTab.ALL_NEWS,
          NewsTab.DRAFT_NEWS,
          NewsTab.SAVED_NEWS,
          NewsTab.PINNED_NEWS,
        ]
      : [
          NewsTab.LATEST_NEWS,
          NewsTab.ALL_NEWS,
          NewsTab.DRAFT_NEWS,
          NewsTab.SAVED_NEWS,
          NewsTab.PINNED_NEWS,
        ]
    : allTabs;

  const {
    data: allNewsData,
    isLoading: isLoadingAllNews,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAllNews(searchText, groupId);

  const allNewsArticles: BlocksApiNews[] =
    allNewsData?.pages.flatMap((page) => page.items) ?? [];

  const { savedNews, isSaved, isLoadingSavedNews } =
    useNewsSaveState(searchText);
  const { isRead } = useNewsReadState();
  const { mutate: toggleSaveBase } = useToggleSaved(userId, "news");
  const { data: draftNews = [], isLoading: isLoadingDraftNews } =
    useDraftNews(searchText);
  const { data: categories = [] } = useNewsCategoriesQuery();
  const { mutate: deleteNews } = useDeleteNews();

  const { mutate: pinNews } = useTogglePinNews();

  useEffect(() => {
    if (isMobile && activeTab === NewsTab.LATEST_NEWS) {
      setActiveTab(NewsTab.ALL_NEWS);
    }
    if (!isAdmin && activeTab === NewsTab.DRAFT_NEWS) {
      setActiveTab(NewsTab.ALL_NEWS);
    }
  }, [isMobile, activeTab, isAdmin]);

  useEffect(() => {
    setSelectedCategoryId("");
    setLocalUnsavedIds([]);
  }, [activeTab]);

  const togglePin = (newsItem: BlocksApiNews) => {
    pinNews({ itemId: newsItem.ItemId, isPinned: !newsItem.isPinned });
  };

  const toggleSave = (articleId: string) => {
    if (isSaved(articleId)) {
      setLocalUnsavedIds((prev) => [...prev, articleId]);
    }
    toggleSaveBase(articleId);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setLocalDeletedIds((prev) => [...prev, deleteTarget.ItemId]);
    deleteNews({ itemId: deleteTarget.ItemId, entity: deleteTarget });
    setDeleteTarget(null);
  };

  const handleEdit = (newsItem: BlocksApiNews) => {
    router.push({
      pathname: "/(tabs)/news/news-creation",
      params: {
        draftId: newsItem.ItemId,
        Title: newsItem.Title,
        Content: newsItem.Content,
        categoryIds: JSON.stringify(newsItem.categoryIds ?? []),
        coverImageUrl: newsItem.coverImageUrl ?? "",
        attachments: JSON.stringify(newsItem.attachmentUrls ?? []),
        // See use-news-detail: omitting this made the form default to unpinned
        // and saving silently unpinned the article.
        isPinned: String(newsItem.isPinned ?? false),
        // Tells the creation screen whether this article is still a draft, so
        // it can hide "Save as Draft" when editing an already-published one.
        isDraft: String(newsItem.isDraft ?? false),
      },
    });
  };

  const isOwner = (newsItem: BlocksApiNews) =>
    !!userId && newsItem.CreatedBy === userId;

  const filterByCategory = (items: BlocksApiNews[]) => {
    if (!selectedCategoryId) return items;
    return items.filter((item) =>
      (item.categoryIds ?? []).includes(selectedCategoryId),
    );
  };

  const removeDeletedAndUnsaved = (
    items: BlocksApiNews[],
    isSavedTab: boolean,
  ) =>
    items.filter((item) => {
      if (localDeletedIds.includes(item.ItemId)) return false;
      if (isSavedTab && localUnsavedIds.includes(item.ItemId)) return false;
      return true;
    });

  const allNewsActive = removeDeletedAndUnsaved(
    filterByCategory(allNewsArticles),
    false,
  ).sort(
    (newer, older) =>
      new Date(older.CreatedDate).getTime() -
      new Date(newer.CreatedDate).getTime(),
  );

  // Pinned + Unread compose: category/deleted/unsaved filtering runs first,
  // then read-state narrows further — pinned-ness (isPinned) and read-state
  // (newsRead) are unrelated fields, so this never overrides the pinned list
  // itself, only what's visible from it.
  const pinnedNewsFiltered =
    pinnedReadFilter === "unread"
      ? filterByCategory(pinnedNews).filter((item) => !isRead(item.ItemId))
      : filterByCategory(pinnedNews);

  const otherTabActive = removeDeletedAndUnsaved(
    activeTab === NewsTab.SAVED_NEWS
      ? filterByCategory(savedNews)
      : activeTab === NewsTab.PINNED_NEWS
        ? pinnedNewsFiltered
        : draftNews,
    activeTab === NewsTab.SAVED_NEWS,
  ).sort(
    (newer, older) =>
      new Date(older.CreatedDate).getTime() -
      new Date(newer.CreatedDate).getTime(),
  );

  const activeNews =
    activeTab === NewsTab.ALL_NEWS || activeTab === NewsTab.LATEST_NEWS
      ? allNewsActive
      : otherTabActive;

  const latestArticles = removeDeletedAndUnsaved(
    filterByCategory([...allNewsArticles]),
    false,
  )
    .sort(
      (newer, older) =>
        new Date(older.CreatedDate).getTime() -
        new Date(newer.CreatedDate).getTime(),
    )
    .slice(0, LATEST_NEWS_COUNT);

  const visibleCategorySource =
    activeTab === NewsTab.LATEST_NEWS
      ? latestArticles
      : activeTab === NewsTab.ALL_NEWS
        ? allNewsActive
        : activeTab === NewsTab.SAVED_NEWS || activeTab === NewsTab.PINNED_NEWS
          ? otherTabActive
          : [];

  const visibleCategories = categories.filter((cat) =>
    visibleCategorySource.some((article) =>
      (article.categoryIds ?? []).includes(cat.categoryID),
    ),
  );
  const visibleCategoryNames = visibleCategories.map((cat) => cat.name);

  const featuredArticle =
    activeTab === NewsTab.LATEST_NEWS ? latestArticles[0] : undefined;
  const remainingLatestArticles =
    activeTab === NewsTab.LATEST_NEWS ? latestArticles.slice(1) : [];

  const isLoading =
    activeTab === NewsTab.DRAFT_NEWS
      ? isLoadingDraftNews
      : activeTab === NewsTab.SAVED_NEWS
        ? isLoadingSavedNews
        : activeTab === NewsTab.PINNED_NEWS
          ? isLoadingPinnedNews
          : isLoadingAllNews;

  const handleCategoryPress = (categoryName: string) => {
    if (categoryName === "__all__") {
      setSelectedCategoryId("");
      return;
    }
    const matchedCategory = categories.find((cat) => cat.name === categoryName);
    setSelectedCategoryId(matchedCategory?.categoryID ?? "");
  };

  const resolveArticleCategoryNames = (newsItem: BlocksApiNews): string[] =>
    (newsItem.categoryIds ?? [])
      .map((id) => categories.find((cat) => cat.categoryID === id)?.name)
      .filter((name): name is string => !!name);

  return {
    searchText,
    setSearchText,
    activeTab,
    setActiveTab,
    deleteTarget,
    setDeleteTarget,
    isMobile,
    tabs,
    isAdmin,
    globalNewsData,
    isLoadingGlobalNews,
    isGlobalNewsError,
    activeNews,
    featuredArticle,
    remainingLatestArticles,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    visibleCategoryNames,
    isLoading,
    isSaved,
    isOwner,
    isRead,
    pinnedReadFilter,
    setPinnedReadFilter,
    toggleSave,
    confirmDelete,
    handleEdit,
    handleCategoryPress,
    resolveArticleCategoryNames,
    togglePin,
  };
}
